import React, { FC } from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export interface IWizardItem {
  title: string;
  description: string;
  icon: any;
  to: string;
}

export interface IProps {
  currentStep: number;
  wizardItems: IWizardItem[];
}

const Wizard: FC<IProps> = ({ currentStep, wizardItems }: IProps) => {
  const { t } = useTranslation();
  // Calculate current progress bar progress based on current step
  const currentProgress = (100 / (wizardItems.length - 1)) * (currentStep - 1);
  return (
    <StepsHolder>
      <ProgressHolder>
        <ProgressBar
          now={currentProgress}
          className="c-progress"
          isChild
        />
      </ProgressHolder>
      {wizardItems.map((item: IWizardItem, index: number) => {
        return (
          <StepHolder key={`${item.description}-${item.to}-${item.title}-${index}`}>
            <IconHolder className={currentStep > index ? 'active' : ''}>
              <item.icon
                size={24}
                color="white"
              />
            </IconHolder>
            <TextHolder>
              <h5>{`${index + 1}. ${t(item.title)}`}</h5>
              <div>{t(item.description)}</div>
            </TextHolder>
          </StepHolder>
        );
      })}
    </StepsHolder>
  );
};

const StepsHolder = styled.div`
  width: 80%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  position: relative;

  @media ${props => props.theme.breakpoints.lg} {
    width: 100%;
    flex-direction: column;
    overflow: hidden;
  }
`;

const ProgressHolder = styled.div`
  position: absolute;
  width: 80%;
  margin: 0 auto;
  left: 10%;
  top: 30px;
  background-color: ${props => props.theme.colors.light};

  .c-progress {
    height: 0.25rem;
  }

  @media ${props => props.theme.breakpoints.lg} {
    transform: rotate(90deg);
    transform-origin: top left;
    left: 0;
    margin-left: 32px;
  }

  @media ${props => props.theme.breakpoints.sm} {
    width: 130%;
  }
`;

const StepHolder = styled.div`
  width: 180px;
  text-align: center;
  z-index: 1;

  @media ${props => props.theme.breakpoints.lg} {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    text-align: left;
    margin: 1rem 0;

    &:last-of-type {
      position: relative;

      &::after {
        position: absolute;
        z-index: -1;
        top: 50%;
        margin-top: 1rem;
        left: 0;
        content: '';
        width: 100%;
        height: 50%;
        background-color: #fff;
      }
    }
  }
`;

const IconHolder = styled.div`
  width: 60px;
  height: 60px;
  background: #ced4da;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;

  &.active {
    background: #32bfa3;
  }

  @media ${props => props.theme.breakpoints.lg} {
    margin-bottom: 0;
    margin-right: 1rem;
  }

  @media ${props => props.theme.breakpoints.sm} {
    position: relative;
    height: auto;
    width: 60px;

    &:before {
      content: '';
      display: block;
      padding-top: 100%;
    }
  }
`;

const TextHolder = styled.div`
  @media ${props => props.theme.breakpoints.sm} {
    width: 70%;
    margin: 0 auto;
  }
`;

export default Wizard;
